import { getApiClient } from '../api-client-factory';
import { API_ENDPOINTS } from '../api-endpoints';
import type { ApiResponse, PaginatedResponse } from '../api-types';
import type {
  ConnectionWithUser,
  ConnectionPerson,
  ConnectionSuggestion,
  DiscoverPersonResult,
  ConnectionState,
  ListConnectionsParams,
  DiscoverPeopleParams,
  MutualConnectionsResult,
} from '@welynk/types';

export type {
  ConnectionWithUser,
  ConnectionPerson,
  ConnectionSuggestion,
  DiscoverPersonResult,
  ConnectionState,
  ListConnectionsParams,
  DiscoverPeopleParams,
  MutualConnectionsResult,
};

export interface PageParams {
  page?: number;
  pageSize?: number;
}

export class ConnectionsApiService {
  static async listConnections(
    params?: ListConnectionsParams
  ): Promise<PaginatedResponse<ConnectionWithUser>> {
    const client = getApiClient();
    const response = await client.get(API_ENDPOINTS.CONNECTIONS.LIST, {
      // Omit favoritesOnly entirely unless it's on — the API treats absence as "all".
      params: params ? { ...params, favoritesOnly: params.favoritesOnly || undefined } : undefined,
    });
    return response.data.data;
  }

  static async removeConnection(id: string): Promise<void> {
    const client = getApiClient();
    await client.delete(API_ENDPOINTS.CONNECTIONS.REMOVE(id));
  }

  static async setFavorite(id: string, favorite: boolean): Promise<void> {
    const client = getApiClient();
    await client.put(API_ENDPOINTS.CONNECTIONS.FAVORITE(id), { favorite });
  }

  static async listPendingReceived(
    params?: PageParams
  ): Promise<PaginatedResponse<ConnectionWithUser>> {
    const client = getApiClient();
    const response = await client.get(API_ENDPOINTS.CONNECTIONS.REQUESTS_RECEIVED, { params });
    return response.data.data;
  }

  static async listPendingSent(
    params?: PageParams
  ): Promise<PaginatedResponse<ConnectionWithUser>> {
    const client = getApiClient();
    const response = await client.get(API_ENDPOINTS.CONNECTIONS.REQUESTS_SENT, { params });
    return response.data.data;
  }

  static async sendRequest(addresseeId: string, message?: string): Promise<void> {
    const client = getApiClient();
    await client.post(API_ENDPOINTS.CONNECTIONS.SEND_REQUEST, {
      addresseeId,
      message: message?.trim() || undefined,
    });
  }

  static async acceptRequest(id: string): Promise<void> {
    const client = getApiClient();
    await client.post(API_ENDPOINTS.CONNECTIONS.ACCEPT_REQUEST(id));
  }

  static async declineRequest(id: string): Promise<void> {
    const client = getApiClient();
    await client.post(API_ENDPOINTS.CONNECTIONS.DECLINE_REQUEST(id));
  }

  static async cancelRequest(id: string): Promise<void> {
    const client = getApiClient();
    await client.delete(API_ENDPOINTS.CONNECTIONS.CANCEL_REQUEST(id));
  }

  static async getStatus(userId: string): Promise<ConnectionState> {
    const client = getApiClient();
    const response = await client.get<ApiResponse<{ status: ConnectionState }>>(
      API_ENDPOINTS.CONNECTIONS.STATUS(userId)
    );
    return response.data.data.status;
  }

  static async discoverPeople(
    params?: DiscoverPeopleParams
  ): Promise<PaginatedResponse<DiscoverPersonResult>> {
    const client = getApiClient();
    const response = await client.get(API_ENDPOINTS.CONNECTIONS.DISCOVER, { params });
    return response.data.data;
  }

  static async getSuggestions(limit = 12): Promise<ConnectionSuggestion[]> {
    const client = getApiClient();
    const response = await client.get<ApiResponse<{ items: ConnectionSuggestion[] }>>(
      API_ENDPOINTS.CONNECTIONS.SUGGESTIONS,
      { params: { limit } }
    );
    return response.data.data.items;
  }

  static async getMutualConnections(userId: string, limit = 6): Promise<MutualConnectionsResult> {
    const client = getApiClient();
    const response = await client.get<ApiResponse<MutualConnectionsResult>>(
      API_ENDPOINTS.CONNECTIONS.MUTUAL(userId),
      { params: { limit } }
    );
    return response.data.data;
  }

  static async listBlocked(params?: PageParams): Promise<PaginatedResponse<ConnectionPerson>> {
    const client = getApiClient();
    const response = await client.get(API_ENDPOINTS.CONNECTIONS.BLOCKED, { params });
    return response.data.data;
  }

  static async blockUser(userId: string): Promise<void> {
    const client = getApiClient();
    await client.post(API_ENDPOINTS.CONNECTIONS.BLOCK(userId));
  }

  static async unblockUser(userId: string): Promise<void> {
    const client = getApiClient();
    await client.delete(API_ENDPOINTS.CONNECTIONS.UNBLOCK(userId));
  }
}
